import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Film, Play, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  documentary: 'Documentary',
  club_story: 'Club Story',
  rising_star: 'Rising Star',
  competition: 'Competition',
  culture: 'Culture',
};

const TYPE_FILTERS = ['all', 'documentary', 'club_story', 'rising_star', 'competition', 'culture'];

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  return `${m}m`;
}

export default function Originals() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const params = filter !== 'all' ? `?original_type=${filter}` : '';
    fetch(`${API}/content/originals${params}`)
      .then(r => r.json())
      .then(d => setItems(d.originals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  const filtered = items;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        {/* Header */}
        <div className="mb-8" data-testid="originals-header">
          <div className="flex items-center gap-3 mb-2">
            <Film className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">AfroSports Originals</h1>
          </div>
          <p className="text-gray-400 text-sm ml-9">Documentaries & stories celebrating African sport</p>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="originals-filters">
          {TYPE_FILTERS.map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setLoading(true); }}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/10'
              }`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : TYPE_LABELS[f] || f}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-500" data-testid="originals-empty">
            <Film className="w-12 h-12 mx-auto mb-3 text-gray-600" />
            <p className="text-sm">No originals found for this category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="originals-grid">
            {filtered.map(item => (
              <div
                key={item.id}
                onClick={() => navigate(`/watch/${item.id}`)}
                className="group cursor-pointer"
                data-testid={`original-card-${item.id}`}
              >
                <div className="relative aspect-video rounded-xl overflow-hidden mb-2.5">
                  <img
                    src={item.thumbnail_url}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-5 h-5 text-black ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex gap-1.5">
                    <span className="px-1.5 py-0.5 bg-purple-600/90 backdrop-blur-sm text-white text-[8px] font-bold uppercase tracking-wider rounded-full">
                      AfroSports Original
                    </span>
                    {item.original_type && TYPE_LABELS[item.original_type] && (
                      <span className="px-1.5 py-0.5 bg-emerald-600/80 backdrop-blur-sm text-white text-[8px] font-bold uppercase rounded-full">
                        {TYPE_LABELS[item.original_type]}
                      </span>
                    )}
                  </div>
                  {/* Duration */}
                  {item.duration && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-black/80 backdrop-blur-sm text-white text-[9px] font-medium rounded">
                      <Clock className="w-2.5 h-2.5" />
                      {formatDuration(item.duration)}
                    </div>
                  )}
                </div>
                <h3 className="text-white font-semibold text-sm group-hover:text-purple-400 transition-colors line-clamp-2">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-xs mt-0.5 line-clamp-1">
                  {item.duration ? formatDuration(item.duration) : ''}
                  {item.original_type ? ` \u00B7 ${TYPE_LABELS[item.original_type] || item.original_type}` : ''}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
